import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core'
import { flashToast } from 'src/app/toast'
import { isActionItem, isAnnotation } from 'src/bookmarks/types'
import { INoteClip } from 'src/call/types'
import { IconName } from 'src/icons'
import { getPlaylistName } from 'src/playlist/utils'
import { Menu, MenuItem } from 'src/ui/menu/menu'

import { copyShareUrl, downloadVideo, getNoteClipShareDisabledReason, NotesStore } from '../state'

@Component({
  tag: 'call-detail-note-menu',
})
export class NoteMenu {
  @Event() delete: EventEmitter
  @Event() changeHighlightTypeClicked: EventEmitter

  @Prop() icon: IconName
  @Prop() clip: INoteClip
  @Prop() allowDelete: boolean
  @Prop() allowShare: boolean
  @Prop() allowDownload: boolean
  @Prop() allowAddToPlaylist: boolean
  @Prop() allowChangeHighlightType: boolean
  @Prop() processing = false

  @State() downloadingMp4 = false
  @State() deletingNote = false
  @State() showAddToPlaylistMenu = false

  menuEl: HTMLUiTooltipElement

  render() {
    const shareUnavailable = !(this.clip.note.actions.activate_share_url || this.clip.note.share_url)
    const downloadUnavailable = !(this.clip.note.actions.activate_download_url || this.clip.note.actions.download_url)

    // If menu has no ability, don't render
    if (
      !(
        this.allowAddToPlaylist ||
        this.allowChangeHighlightType ||
        this.allowDelete ||
        (this.allowDownload && !downloadUnavailable) ||
        (this.allowShare && !shareUnavailable)
      )
    ) {
      // Using hidden=true instead of css hidden or invisible or even null keeps
      // the element from taking up space in the dom. This really only works in
      // this particular case, so remove it if the alignment rules change.
      return <Host hidden={true}></Host>
    }

    const noteShareDisabledReason = getNoteClipShareDisabledReason(this.clip)

    return (
      <Host>
        <ui-icon icon={this.icon} class='relative'>
          <Menu ref={(el) => (this.menuEl = el)} anchor='top' name='note-actions' allowClick={false} contentWidth='48'>
            {!this.showAddToPlaylistMenu ? (
              <Fragment>
                {this.allowDelete && (
                  <MenuItem
                    icon='trash'
                    disabled={this.deletingNote}
                    onClick={async () => {
                      this.deletingNote = true
                      this.delete.emit()
                    }}
                  >
                    <span class='text-sm'>
                      Delete{' '}
                      {isActionItem(this.clip.note.bookmark)
                        ? 'Action Item'
                        : isAnnotation(this.clip.note.bookmark)
                        ? 'Annotation'
                        : 'Clip'}
                    </span>
                  </MenuItem>
                )}
                {this.allowChangeHighlightType && (
                  <MenuItem
                    icon='list'
                    onClick={(e) => {
                      e.stopImmediatePropagation()
                      this.changeHighlightTypeClicked.emit()
                    }}
                  >
                    <span class='text-sm'>Change Highlight Type</span>
                  </MenuItem>
                )}
                {this.allowDownload && (
                  <MenuItem
                    icon='download'
                    disabled={downloadUnavailable || this.downloadingMp4}
                    onClick={async () => {
                      this.downloadingMp4 = true
                      await downloadVideo(this.clip)
                      this.downloadingMp4 = false
                    }}
                    disabledReason={
                      downloadUnavailable
                        ? 'Download is not available for this clip'
                        : this.downloadingMp4 && 'Generating download...'
                    }
                  >
                    <span class='text-sm'>Download Video Clip (mp4)</span>
                  </MenuItem>
                )}
                {this.allowAddToPlaylist && (
                  <MenuItem
                    icon='add-to-playlist'
                    onClick={() => {
                      this.showAddToPlaylistMenu = true
                    }}
                  >
                    <span class='text-sm'>Add to Playlist</span>
                  </MenuItem>
                )}
                {this.allowShare && (
                  <MenuItem
                    disabled={!!noteShareDisabledReason}
                    icon='link-45deg'
                    onClick={async () => {
                      await copyShareUrl(this.clip)
                    }}
                    disabledReason={noteShareDisabledReason}
                  >
                    <span class='text-sm'>Copy Share Link</span>
                  </MenuItem>
                )}
                <slot />
              </Fragment>
            ) : (
              <add-to-playlist-menu
                createPlaylistUrl={NotesStore.state.playlistActions.createUrl}
                item={{ id: this.clip.note.id, type: 'highlight' }}
                listPlaylistsUrl={NotesStore.state.playlistActions.indexUrl}
                onMenuClosed={() => {
                  this.showAddToPlaylistMenu = false
                }}
                onAdded={(e) => {
                  flashToast({ type: 'success', text: `Added highlight to ${getPlaylistName(e?.detail)}` })
                  this.showAddToPlaylistMenu = false
                  this.menuEl?.close()
                }}
                onFailed={(e) => {
                  flashToast({ type: 'alert', text: `Failed to add highlight to ${getPlaylistName(e?.detail)}` })
                  this.showAddToPlaylistMenu = false
                  this.menuEl?.close()
                }}
              />
            )}
          </Menu>
          {this.processing && (
            <div class='flex absolute inset-0 z-10 text-white'>
              <ui-spinner class='m-auto' />
            </div>
          )}
        </ui-icon>
      </Host>
    )
  }
}
